/**
 * Typed loaders for public/data/*.json.
 *
 *   await data.init();            // fetch everything the game needs (once, behind a black frame)
 *   const maps = data.maps();     // synchronous accessors afterwards
 *
 * Or fetch one file directly: `await loadMaps()`. Game code under src/game/ must not call
 * fetch itself (PORTING_RULES §2); it uses these accessors after init.
 */
import { DATA_FILES, type DataFiles } from './types';
import { applyMapNames, applyPatches, setFlags } from './patches';

export type DataKey = keyof DataFiles;

/** Files the runtime needs. mapDefsRaw / mergeReport are porting references, not loaded. */
export const RUNTIME_KEYS: readonly DataKey[] = [
  'maps', 'dialogue', 'dialogueOverrides', 'voice', 'story', 'quests', 'chapters',
  'profileCatalogs', 'cards', 'constants', 'encounters', 'trivia', 'fishing',
  'settingsDefaults', 'storyText', 'mapDefsRaw', 'mapOverrides', 'patches',
];

let baseUrl = './data/';
const cache: Partial<{ [K in DataKey]: DataFiles[K] }> = {};
const inflight: Partial<Record<DataKey, Promise<unknown>>> = {};

export function setDataBase(url: string): void {
  baseUrl = url.endsWith('/') ? url : url + '/';
}

export async function loadFile<K extends DataKey>(key: K): Promise<DataFiles[K]> {
  const hit = cache[key];
  if (hit !== undefined) return hit as DataFiles[K];
  let p = inflight[key] as Promise<DataFiles[K]> | undefined;
  if (!p) {
    p = (async () => {
      const res = await fetch(baseUrl + DATA_FILES[key]);
      if (!res.ok) throw new Error(`data: ${DATA_FILES[key]} -> HTTP ${res.status}`);
      const json = (await res.json()) as DataFiles[K];
      if (key === 'patches') setFlags((json as DataFiles['patches']).flags);
      if (key === 'mapDefsRaw') {
        // RULING 2026-10-09 (arcana_sw): patches with file 'map_defs' (interactable / gated-object tiles).
        applyPatches((json as any).maps, await loadFile('patches'), undefined, 'map_defs');
      }
      if (key === 'profileCatalogs') {
        // Map titles the Python MAP_NAMES never had (interiors read "Home4"): patches.json mapNames.
        applyMapNames((json as DataFiles['profileCatalogs']).mapNames, await loadFile('patches'));
      }
      if (key === 'mapOverrides') {
        // Hand-authored data fixes (docs/DATA_PIPELINE.md "Patches"): asserted, so a stale patch throws.
        applyPatches(json as DataFiles['mapOverrides'], await loadFile('patches'));
      }
      cache[key] = json;
      return json;
    })();
    inflight[key] = p;
  }
  return p;
}

/** Inject parsed JSON (tests, node tools). */
export function provide<K extends DataKey>(key: K, value: DataFiles[K]): void {
  cache[key] = value;
}

function need<K extends DataKey>(key: K): DataFiles[K] {
  const v = cache[key];
  if (v === undefined) throw new Error(`data.${key}() before data.init() loaded ${DATA_FILES[key]}`);
  return v as DataFiles[K];
}

export const loadMaps = () => loadFile('maps');
export const loadMapDefsRaw = () => loadFile('mapDefsRaw');
export const loadMergeReport = () => loadFile('mergeReport');
export const loadDialogue = () => loadFile('dialogue');
export const loadDialogueOverrides = () => loadFile('dialogueOverrides');
export const loadVoice = () => loadFile('voice');
export const loadStory = () => loadFile('story');
export const loadQuests = () => loadFile('quests');
export const loadChapters = () => loadFile('chapters');
export const loadProfileCatalogs = () => loadFile('profileCatalogs');
export const loadCards = () => loadFile('cards');
export const loadConstants = () => loadFile('constants');
export const loadEncounters = () => loadFile('encounters');
export const loadTrivia = () => loadFile('trivia');
export const loadFishing = () => loadFile('fishing');
export const loadSettingsDefaults = () => loadFile('settingsDefaults');
export const loadStoryText = () => loadFile('storyText');
export const loadMapOverrides = () => loadFile('mapOverrides');

export const data = {
  /** Fetch every runtime file (or the given subset) in parallel. */
  async init(keys: readonly DataKey[] = RUNTIME_KEYS): Promise<void> {
    await Promise.all(keys.map((k) => loadFile(k)));
  },
  isLoaded(key: DataKey): boolean { return cache[key] !== undefined; },
  maps: () => need('maps'),
  mapDefsRaw: () => need('mapDefsRaw'),
  mergeReport: () => need('mergeReport'),
  dialogue: () => need('dialogue'),
  dialogueOverrides: () => need('dialogueOverrides'),
  voice: () => need('voice'),
  story: () => need('story'),
  quests: () => need('quests'),
  chapters: () => need('chapters'),
  profileCatalogs: () => need('profileCatalogs'),
  cards: () => need('cards'),
  constants: () => need('constants'),
  encounters: () => need('encounters'),
  trivia: () => need('trivia'),
  fishing: () => need('fishing'),
  settingsDefaults: () => need('settingsDefaults'),
  storyText: () => need('storyText'),
  mapOverrides: () => need('mapOverrides'),
  /** One map after the shipping merge. */
  map(name: string) {
    const m = need('maps').maps[name];
    if (!m) throw new Error(`data.map: unknown map ${name}`);
    return m;
  },
  /** One dialog callable by ref (npc.dialog / enemy.pre_dialog / ...). */
  dialog(ref: string) {
    const c = need('dialogue').callables[ref];
    if (!c) throw new Error(`data.dialog: unknown ref ${ref}`);
    return c;
  },
};
